import { useState } from "react";
import { Link } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { Input, NumericTextBox } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { apiClient } from "src/dm/utils/axios";

interface SingleResult {
  dealId: number;
  newOwnerUserId: string;
  affected: number;
}

interface BulkResult {
  affected: number;
  newOwnerUserId: string;
  filter: {
    status: string | null;
    bannerCode: string | null;
    currentOwnerUserId: string | null;
  };
}

const baseUrl = "/dm/V1/admin/deal-owner-changes";

const DealOwnerChangesPage = () => {
  // Single-deal state.
  const [dealId, setDealId] = useState<number | null>(null);
  const [singleNewOwner, setSingleNewOwner] = useState("");

  // Bulk state.
  const [status, setStatus] = useState("");
  const [bannerCode, setBannerCode] = useState("");
  const [currentOwner, setCurrentOwner] = useState("");
  const [bulkNewOwner, setBulkNewOwner] = useState("");
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  const single = useMutation({
    mutationFn: async (): Promise<SingleResult> => {
      const response = await apiClient.post<SingleResult>(`${baseUrl}/single`, {
        dealId,
        newOwnerUserId: singleNewOwner,
      });
      return response.data;
    },
  });

  const bulk = useMutation({
    mutationFn: async (): Promise<BulkResult> => {
      const response = await apiClient.post<BulkResult>(`${baseUrl}/bulk`, {
        filter: {
          status: status.trim() || null,
          bannerCode: bannerCode.trim() || null,
          currentOwnerUserId: currentOwner.trim() || null,
        },
        newOwnerUserId: bulkNewOwner,
      });
      return response.data;
    },
    onSuccess: () => setBulkConfirmOpen(false),
  });

  const filterSummary = [
    status.trim() ? `status=${status.trim()}` : null,
    bannerCode.trim() ? `banner=${bannerCode.trim()}` : null,
    currentOwner.trim() ? `currentOwner=${currentOwner.trim()}` : null,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      <Link
        to="/dm/admin"
        style={{
          fontSize: "0.85rem",
          color: "#0a3d62",
          textDecoration: "none",
        }}
      >
        ← Admin
      </Link>
      <h1 style={{ margin: 0 }}>Deal Owner Changes</h1>
      <p style={{ color: "#475569", marginTop: "-0.5rem" }}>
        Re-assign deal ownership for a single deal or in bulk by filter.
      </p>

      <section
        style={{
          background: "#fff",
          padding: "1rem 1.25rem",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        <h2 style={{ marginTop: 0, fontSize: "1.05rem" }}>Single deal</h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "0.5rem 0.75rem",
          }}
        >
          <label>
            <span style={{ fontSize: "0.8rem", color: "#475569" }}>
              Deal ID
            </span>
            <NumericTextBox
              value={dealId}
              format="n0"
              onChange={(e) => setDealId(e.value ?? null)}
            />
          </label>
          <label>
            <span style={{ fontSize: "0.8rem", color: "#475569" }}>
              New owner user ID
            </span>
            <Input
              value={singleNewOwner}
              onChange={(e) => setSingleNewOwner(String(e.target.value ?? ""))}
              placeholder="GiantEagle\\username"
            />
          </label>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "0.75rem",
          }}
        >
          <Button
            themeColor="primary"
            disabled={single.isPending || !dealId || !singleNewOwner.trim()}
            onClick={() => single.mutate()}
          >
            {single.isPending ? "Updating…" : "Update owner"}
          </Button>
        </div>
        {single.isError && (
          <pre style={{ color: "#b91c1c", fontSize: "0.8rem" }}>
            {single.error instanceof Error
              ? single.error.message
              : "Update failed"}
          </pre>
        )}
        {single.isSuccess && (
          <p style={{ color: "#15803d", fontSize: "0.85rem", marginTop: "0.5rem" }}>
            Updated deal {single.data?.dealId} to {single.data?.newOwnerUserId}{" "}
            ({single.data?.affected} row).
          </p>
        )}
      </section>

      <section
        style={{
          background: "#fff",
          padding: "1rem 1.25rem",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        <h2 style={{ marginTop: 0, fontSize: "1.05rem" }}>Bulk re-assign</h2>
        <p style={{ color: "#64748b", fontSize: "0.8rem", marginTop: 0 }}>
          At least one filter (status, banner, or current owner) is required.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: "0.5rem 0.75rem",
          }}
        >
          <label>
            <span style={{ fontSize: "0.8rem", color: "#475569" }}>Status</span>
            <Input
              value={status}
              onChange={(e) => setStatus(String(e.target.value ?? ""))}
              placeholder="e.g. APPROVED"
            />
          </label>
          <label>
            <span style={{ fontSize: "0.8rem", color: "#475569" }}>
              Banner code
            </span>
            <Input
              value={bannerCode}
              onChange={(e) => setBannerCode(String(e.target.value ?? ""))}
            />
          </label>
          <label>
            <span style={{ fontSize: "0.8rem", color: "#475569" }}>
              Current owner user ID
            </span>
            <Input
              value={currentOwner}
              onChange={(e) => setCurrentOwner(String(e.target.value ?? ""))}
            />
          </label>
          <label style={{ gridColumn: "1 / -1" }}>
            <span style={{ fontSize: "0.8rem", color: "#475569" }}>
              New owner user ID
            </span>
            <Input
              value={bulkNewOwner}
              onChange={(e) => setBulkNewOwner(String(e.target.value ?? ""))}
              placeholder="GiantEagle\\username"
            />
          </label>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "0.75rem",
          }}
        >
          <Button
            themeColor="warning"
            disabled={
              bulk.isPending ||
              !bulkNewOwner.trim() ||
              (!status.trim() && !bannerCode.trim() && !currentOwner.trim())
            }
            onClick={() => setBulkConfirmOpen(true)}
          >
            Re-assign in bulk
          </Button>
        </div>
        {bulk.isError && (
          <pre style={{ color: "#b91c1c", fontSize: "0.8rem" }}>
            {bulk.error instanceof Error
              ? bulk.error.message
              : "Bulk update failed"}
          </pre>
        )}
        {bulk.isSuccess && (
          <p style={{ color: "#15803d", fontSize: "0.85rem", marginTop: "0.5rem" }}>
            Re-assigned {bulk.data?.affected} deal(s) to{" "}
            {bulk.data?.newOwnerUserId}.
          </p>
        )}
      </section>

      {bulkConfirmOpen && (
        <Dialog
          title="Confirm bulk re-assignment"
          onClose={() => setBulkConfirmOpen(false)}
        >
          <p style={{ margin: "0 0 0.5rem", maxWidth: "440px" }}>
            This will update every deal matching{" "}
            <code>{filterSummary || "(no filter)"}</code> to be owned by{" "}
            <code>{bulkNewOwner}</code>. Continue?
          </p>
          <DialogActionsBar>
            <Button onClick={() => setBulkConfirmOpen(false)}>Cancel</Button>
            <Button
              themeColor="error"
              disabled={bulk.isPending}
              onClick={() => bulk.mutate()}
            >
              {bulk.isPending ? "Re-assigning…" : "Yes, re-assign"}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
};

export default DealOwnerChangesPage;
